import {AspectRatio} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/aspect_ratio.types';
import {ProductImage as ProductImageData} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {
  PageItem_OutfitContentBlock,
  PageItem_OutfitContentBlock_Outfit,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackProduct, TrackPromotions} from '@aboutyou/tracking';
import {
  Button,
  ContentBlockVideoAutoPlay,
  GRID_GUTTER_WIDTH,
  HorizontalSlider,
  ImageRatio,
  OutfitCollage,
  ProductImage,
  RADIUS,
  ROW_SECTION_MAX_WIDTH,
  SPACING,
  SliderControlProps,
  SliderPaginationBase,
  SliderWrapper,
  StyledProductImage,
  UnstyledLink,
  gridCols,
  gridColsWidth,
  gridContainer,
  mediaLargeScreens,
  useMatchesMediaQuery,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback} from 'react';
import {useBadgesListKeys} from '../../../features/BadgeList/useBadgesList';
import {hasPlayableVideo} from '../../../features/BadgeList/utils';
import {StreamTileListProps} from '../../../features/ProductStream/StreamTileList';
import {InteractiveProductTile} from '../../../features/ProductTile/InteractiveProductTile';

interface OutfitProps {
  outfit: PageItem_OutfitContentBlock_Outfit;
  streamPosition: number;
  testId: string;
  onClickAddCta?: StreamTileListProps['onClickAddCta'];
  addCtaLabel?: string;
}

const OUTFIT_PRODUCT_RECO_TYPE = 'product_stream';

export const StoryOutfit: React.FC<{
  content: PageItem_OutfitContentBlock;
  streamPosition: number;
  testId?: string;
  onClickAddCta?: StreamTileListProps['onClickAddCta'];
  addCtaLabel?: string;
}> = ({
  content,
  streamPosition,
  testId = 'storyOutfit',
  onClickAddCta,
  addCtaLabel,
}) => {
  if (content.outfits.length === 0) return null;

  const Outfit = process.env.STACK === 'desktop' ? DesktopOutfit : MobileOutfit;

  return (
    <>
      {content.outfits.map((outfit, index) => (
        <Outfit
          key={outfit.bundleId || index}
          outfit={outfit}
          streamPosition={streamPosition}
          testId={index === 0 ? testId : `${testId}-${index}`}
          onClickAddCta={onClickAddCta}
          addCtaLabel={addCtaLabel}
        />
      ))}
    </>
  );
};

const DesktopOutfit: React.FC<OutfitProps> = ({
  outfit,
  streamPosition,
  testId,
  onClickAddCta,
  addCtaLabel,
}) => {
  const {t} = useTranslation();
  const {media, outfitPageLink, products, promotionTracker} = outfit;

  const handleClickAddCta = useCallback(
    (tile: ProductTileV2) => () => {
      onClickAddCta?.({
        productId: tile.productId,
        filteredSizeId: tile.selectedSizeId,
        couponPromotionId: tile.couponPromotionId,
        recoType: OUTFIT_PRODUCT_RECO_TYPE,
        streamPosition,
        locationId: OUTFIT_PRODUCT_RECO_TYPE,
      });
    },
    [onClickAddCta, streamPosition],
  );

  // Below the large-screens breakpoint the page splits 4:8 instead of 2:10,
  // leaving the stream 8 columns: only one 3-column tile fits next to the image.
  const isLargeScreen = useMatchesMediaQuery(mediaLargeScreens);
  const visibleProductTiles = isLargeScreen ? 2 : 1;

  const primaryImage = getPrimaryImage(media);
  const primaryVideo = getPrimaryVideo(media);

  if (!primaryImage || products.length === 0) return null;

  const hasPagination = products.length > visibleProductTiles;
  const coverRatio = primaryVideo?.aspectRatio ?? primaryImage.aspectRatio;

  return (
    <Row
      data-testid={testId}
      promotion={{
        source: TrackingPromotionCreative.GenericPromotion,
        promotionTracker,
      }}
      streamPosition={streamPosition}
    >
      <ImageFrame
        data-below-pagination={hasPagination}
        $aspectRatio={coverRatio}
      >
        <OutfitLink
          to={outfitPageLink.url}
          openExternalLinkInNewTab={outfitPageLink.external}
          aria-label={t('Go to the outfit page')}
        >
          {primaryVideo ? (
            <CoverVideo
              testId={`${testId}Video`}
              video={primaryVideo}
              disablePlayPauseControl
              disableMuteUnmuteControl
            />
          ) : (
            <CoverImage src={primaryImage.src} alt="" />
          )}
          <CtaOverlay>
            <Button
              data-testid={`${testId}Cta`}
              variant="secondary"
              size="full-width"
              aria-label={undefined}
            >
              {t('See full outfit')}
            </Button>
          </CtaOverlay>
        </OutfitLink>
      </ImageFrame>
      {/* The slider sits inside the block's own click tracker (Row); stop clicks
          here from bubbling up so they don't also fire the block's select_promotion
          on top of a product's own select_item. */}
      <ProductSliderTracker onClick={e => e.stopPropagation()}>
        <ProductSlider
          $imageRatio={coverRatio}
          constrained
          hideSideButtons
          slideCount={visibleProductTiles}
          SlideControlComponent={ProductSliderPagination}
          uniqueKey={testId}
          dataTestId={`${testId}Products`}
        >
          {products.map(tile => (
            <InteractiveProductTile
              key={tile.productId}
              tile={tile}
              streamPosition={streamPosition}
              gridColumns={4}
              trackingData={{recoType: OUTFIT_PRODUCT_RECO_TYPE}}
              addCtaLabel={addCtaLabel}
              onClickAddCta={
                onClickAddCta ? handleClickAddCta(tile) : undefined
              }
            />
          ))}
        </ProductSlider>
      </ProductSliderTracker>
    </Row>
  );
};

const ProductSliderPagination: React.FC<SliderControlProps> = ({
  scrollToNextItem,
  scrollToPrevItem,
  nextIsActive,
  prevIsActive,
  scrollToSlide,
}) => (
  <PaginationPill
    next={scrollToNextItem}
    prev={scrollToPrevItem}
    nextIsActive={nextIsActive}
    prevIsActive={prevIsActive}
    scrollToSlide={scrollToSlide}
  />
);

const MobileOutfit: React.FC<OutfitProps> = ({
  outfit,
  streamPosition,
  testId,
}) => {
  const {t} = useTranslation();
  const {media, outfitPageLink, products, promotionTracker} = outfit;

  const primaryImage = getPrimaryImage(media);
  const primaryVideo = getPrimaryVideo(media);

  const secondaryImages = products
    .map(tile => {
      const firstElement = tile.gallery.elements[0];
      if (firstElement?.type?.$case !== 'image') return null;

      const {image} = firstElement.type.image;

      return {
        element: (
          <MobileOutfitProductLink
            tile={tile}
            streamPosition={streamPosition}
            image={image}
          />
        ),
      };
    })
    .filter((image): image is {element: React.ReactElement} => image !== null);

  if (!primaryImage || secondaryImages.length === 0) return null;

  return (
    <TrackPromotions
      data-testid={`${testId}Tracker`}
      promotion={{
        source: TrackingPromotionCreative.GenericPromotion,
        promotionTracker,
      }}
      streamPosition={streamPosition}
    >
      <Collage
        data-testid={testId}
        ctaLabel={t('See full outfit')}
        ctaVariant="glassy"
        primaryImage={primaryImage}
        primaryVideo={primaryVideo}
        secondaryImages={secondaryImages}
        primaryImageLinkTarget={{
          url: outfitPageLink.url,
          external: outfitPageLink.external,
        }}
      />
    </TrackPromotions>
  );
};

const MobileOutfitProductLink: React.FC<{
  tile: ProductTileV2;
  streamPosition: number;
  image: ProductImageData;
}> = ({tile, streamPosition, image}) => {
  const badges = useBadgesListKeys({
    badges: tile.badges,
    sale: null,
    campaignSale: null,
    useDefaultLabels: true,
    showPriceOverhaulV2SaleLabel: false,
    hasVideo: hasPlayableVideo(tile.gallery),
  });

  return (
    // The link sits inside the outfit's own click tracker (TrackPromotions in
    // MobileOutfit); stop clicks here from bubbling up so they don't also
    // fire the block's select_promotion on top of the product's select_item.
    <ProductTrackerWrapper onClick={e => e.stopPropagation()}>
      <TrackProduct
        Comp={ProductTrackerWrapper}
        productTile={{
          product: tile.productTracker,
          brand: tile.brandTracker,
          price: tile.price,
        }}
        streamPosition={streamPosition}
        trackingKey={tile.productId}
        threshold={0.2}
        triggerOnce
        badges={badges}
        trackingData={{recoType: OUTFIT_PRODUCT_RECO_TYPE}}
        data-testid={`storyOutfitProductTracker-${tile.productId}`}
      >
        <ProductLink
          to={tile.link.url}
          openExternalLinkInNewTab={tile.link.external}
          aria-label={tile.productName ?? image.description}
        >
          <ProductImage src={image.src} alt="" padded={image.isEnclosed} />
        </ProductLink>
      </TrackProduct>
    </ProductTrackerWrapper>
  );
};

const getPrimaryImage = (
  media: PageItem_OutfitContentBlock_Outfit['media'],
) => {
  switch (media?.$case) {
    case 'image':
      return {
        src: media.image.url,
        alt: media.image.description,
        padded: false,
        aspectRatio: toImageRatio(media.image.aspectRatio),
      };
    // Poster fallback, used when the video has no playable sources.
    case 'video':
      return {
        src: media.video.fallbackImage.url,
        alt: media.video.fallbackImage.description || media.video.description,
        padded: false,
        aspectRatio: toImageRatio(media.video.aspectRatio),
      };
    default:
      return null;
  }
};

const getPrimaryVideo = (
  media: PageItem_OutfitContentBlock_Outfit['media'],
) => {
  if (media?.$case !== 'video' || media.video.sources.length === 0) {
    return undefined;
  }

  const {video} = media;
  const aspectRatio = toImageRatio(video.aspectRatio);

  return {
    sources: video.sources,
    lowSources: video.lowSources,
    description: video.description,
    aspectRatio,
    fallbackImage: {
      url: video.fallbackImage.url,
      description: video.fallbackImage.description,
      aspectRatio,
    },
  };
};

const toImageRatio = (ratio: AspectRatio | undefined): ImageRatio =>
  ratio ? `${ratio.width}:${ratio.height}` : '3:4';

const toCssAspectRatio = (ratio: ImageRatio) => ratio.replace(':', ' / ');

// StyledProductImage's own padding-top aspect-ratio box takes a height/width
// percentage (see @aboutyou/ui-components ProductImage.tsx).
const toPaddingTopPercentage = (ratio: ImageRatio) => {
  const [width, height] = ratio.split(':').map(Number);
  return `${((height ?? 0) / (width || 1)) * 100}%`;
};

const Row = styled(TrackPromotions)`
  ${gridContainer}
`;

const PAGINATION_PILL_HEIGHT = '36px';
const PAGINATION_PILL_GAP = SPACING['Y.Md'];

const ImageFrame = styled.div<{$aspectRatio: ImageRatio}>`
  flex-shrink: 0;
  align-self: flex-start;
  aspect-ratio: ${({$aspectRatio}) => toCssAspectRatio($aspectRatio)};
  position: relative;
  overflow: hidden;
  border-radius: ${RADIUS.Md};

  /* The pagination renders inside the slider column, above the tiles; mirror
     its height so the image stays top-aligned with the product tiles. */
  &[data-below-pagination='true'] {
    margin-top: calc(${PAGINATION_PILL_HEIGHT} + ${PAGINATION_PILL_GAP});
  }

  ${gridCols(3)}
`;

const OutfitLink = styled(UnstyledLink)`
  position: absolute;
  inset: 0;
`;

const CoverImage = styled(ProductImage)`
  && {
    position: absolute;
    inset: 0;
    height: 100%;
    padding-top: 0;
    border-radius: 0;
  }
`;

const CoverVideo = styled(ContentBlockVideoAutoPlay)`
  position: absolute;
  inset: 0;
`;

const CtaOverlay = styled.div`
  position: absolute;
  left: ${SPACING['X.Lg']};
  right: ${SPACING['X.Lg']};
  bottom: ${SPACING['X.Xl']};
`;

const PaginationPill = styled(SliderPaginationBase)`
  && {
    height: ${PAGINATION_PILL_HEIGHT};
    margin: 0 0 ${PAGINATION_PILL_GAP};
  }
`;

const ProductSliderTracker = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductSlider = styled(HorizontalSlider)<{$imageRatio: ImageRatio}>`
  /* Per design the slider bleeds off the right edge of the screen instead of
     ending at the grid. --stream-end-bleed is the distance from the stream's
     right edge to the screen's right edge: the RowSection centering margin
     (0 below the max-width cap) plus the page inset — the same inset
     expression gridColsWidth subtracts, keeping grid math and bleed in sync. */
  --stream-end-bleed: calc(
    (100vw - min(${ROW_SECTION_MAX_WIDTH}px, 100vw)) / 2 +
      max(var(--row-section-padding, 0px), ${SPACING['X.Lg']})
  );

  ${SliderWrapper} {
    --spacing: ${GRID_GUTTER_WIDTH};
    /* Widen by the bleed to reach the screen edge... */
    width: calc(100% + var(--stream-end-bleed));
    height: auto;

    /* ...and pad by the same amount so the last tile lines up with the grid
       again when scrolled to the end. */
    padding-right: var(--stream-end-bleed);
  }

  ${SliderWrapper} > * {
    flex-basis: ${gridColsWidth(3)};
  }

  ${StyledProductImage} {
    padding-top: ${({$imageRatio}) => toPaddingTopPercentage($imageRatio)};
  }

  ${SliderWrapper} {
    /* The hover-revealed Add to basket CTA pops out below the tile via
       position:absolute (InteractiveProductTile), but SliderWrapper clips
       vertical overflow (overflow-y:hidden, needed for the horizontal
       scroll) - grow the box so the popup has room, same technique as
       InlineOutfitSlider's StyledHorizontalSlider. Unlike there, this isn't
       gated behind a viewport-width breakpoint: DesktopOutfit only ever
       renders on the desktop stack in the first place (MobileOutfit is a
       separate, hover-free implementation), so there's no narrower/mobile
       case here to exclude - gating it further just reintroduced the
       clipping on a narrower desktop-stack viewport.
    */
    padding-bottom: 48px;
    margin-bottom: -48px;
  }
`;

const Collage = styled(OutfitCollage)`
  width: 100%;
  min-height: 400px;
`;

const ProductLink = styled(UnstyledLink)`
  display: flex;
  flex: 1;
`;

const ProductTrackerWrapper = styled.div`
  display: flex;
  flex: 1;
`;
